import React from 'react';
import { ChevronDown, AlertCircle } from 'lucide-react';

export interface SelectOption {
  label: string;
  value: string | number;
  disabled?: boolean;
}

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: SelectOption[];
  helperText?: string;
  error?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, helperText, error, disabled, className = '', id, ...props }, ref) => {
    const selectId = id || (label ? `select-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    return (
      <div className="w-full space-y-1.5">
        {label && (
          <label htmlFor={selectId} className="block text-xs font-bold text-[#111111] uppercase tracking-wider">
            {label}
          </label>
        )}
        <div className="relative flex items-center">
          <select
            id={selectId}
            ref={ref}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined}
            className={`w-full bg-white text-[#111111] text-xs sm:text-sm font-medium rounded-xl border appearance-none transition-all duration-150 outline-none pl-3.5 pr-9 py-2.5 disabled:bg-[#F1F3F4] disabled:text-[#9AA0A6] disabled:cursor-not-allowed ${
              error
                ? 'border-[#D94A5C] focus:ring-2 focus:ring-[#D94A5C]/20'
                : 'border-[#E5E9E9] focus:border-[#1fb4ac] focus:ring-2 focus:ring-[#1fb4ac]/20'
            } ${className}`}
            {...props}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="absolute right-3 pointer-events-none text-[#5F6368]">
            {error ? <AlertCircle className="w-4 h-4 text-[#D94A5C]" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="text-xs text-[#D94A5C] font-medium">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${selectId}-helper`} className="text-xs text-[#5F6368]">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
